import { useStore } from 'Store';
import { defaultRecStorage } from 'Utils/itemUtils';
import { logEvent } from 'AppManager';

const MAX_RECORDING_STORAGE_PERCENTAGE = 90;

/**
 *
 * @typedef {import('@24i/st-api-connector').ProgramModel } ProgramModel;
 * @param {ProgramModel} item
 */
export const useRecordingCalls = (item) => {
    const { appManager } = useStore();
    const api = appManager.getApi();
    const { tv } = api.backstageConnector;

    return {
        /**
         * @param series
         * @param storage
         */
        record: async (series, storage = defaultRecStorage) => {
            logEvent({
                type: 'action',
                subtype: 'recordingSet',
                content1: item.getId(),
                content2: series ? 'series' : 'single'
            });
            return tv.addProgramRecording({
                scheduleId: item.getId(),
                channelID: item.getChannelId(),
                type: series ? 'SERIES' : 'SIMPLE',
                storage
            });
        },
        /**
         * @param series
         * @param storage
         * @param recordingIds
         */
        cancelRecording: async (series, storage = defaultRecStorage, recordingIds) => {
            logEvent({ type: 'action', subtype: 'recordingDelete', content1: item.getId() });
            if (series) {
                return tv.stopSeriesRecording({
                    seriesId: item.data.seriesId,
                    storage
                });
            }
            return tv.deleteRecording({ recordingIds, storage });
        },
        /**
         * @param series
         * @param storage
         * @param recordingIds
         */
        deleteRecording: async (series, storage = defaultRecStorage, recordingIds) => {
            logEvent({ type: 'action', subtype: 'recordingDelete', content1: item.getId() });
            if (series) {
                return tv.deleteSeriesRecording({
                    seriesId: item.data.seriesId,
                    storage
                });
            }
            return tv.deleteRecording({ recordingIds, storage });
        },
        isRecordingStorageFull: async (storage) => {
            let iptvPercentage = 0;
            let goPercentage = 0;

            try {
                const recordingsResult = await api.fetchAllRecordings({
                    platform: storage,
                    planned: false
                });
                const selectedPlatforms = storage.split(',');
                if (selectedPlatforms.includes('iptv') && recordingsResult.iptv) {
                    const {
                        recordingTimeToUsed: recordingTimeToUsedIptv,
                        recordingTimeTotal: recordingTimeTotalIptv
                    } = recordingsResult.iptv;

                    iptvPercentage = (recordingTimeToUsedIptv / recordingTimeTotalIptv) * 100;
                }

                if (selectedPlatforms.includes('go') && recordingsResult.go) {
                    const {
                        recordingTimeToUsed: recordingTimeToUsedGo,
                        recordingTimeTotal: recordingTimeTotalGo
                    } = recordingsResult.go;

                    goPercentage = (recordingTimeToUsedGo / recordingTimeTotalGo) * 100;
                }
            } catch (error) {
                console.log(error);
            }

            return (
                iptvPercentage > MAX_RECORDING_STORAGE_PERCENTAGE ||
                goPercentage > MAX_RECORDING_STORAGE_PERCENTAGE
            );
        },

        fetchRecordings: async () => {
            try {
                const { items, iptv, go, ids } = await api.fetchAllRecordings({
                    platform: 'iptv,go'
                });

                const { items: plannedItems } = await api.fetchAllRecordings({
                    platform: 'iptv,go',
                    planned: true
                });

                return { items, iptv, go, plannedItems, ids: [...new Set(ids)] };
            } catch (error) {
                console.log(error);

                return {
                    items: [],
                    iptv: null,
                    go: null,
                    plannedItems: [],
                    ids: []
                };
            }
        }
    };
};
